import React, { useState } from 'react';
import styled from 'styled-components';
import { createMatrixItems } from '../../matrixItems';
import { SIGNATURE } from '../thresholdConstants';
import { itemName } from './elements';
import { newFieldKey } from './fieldKeys';
import { dispatch } from '../../../../redux/store';
import { setSaveIsDisabled } from '../../../../redux/slices/thresholdFormSlice';

interface Props {
  data: Record<string, any>;
  setData: (d: Record<string, any>) => void;
  tab: string;
  setSelectedKey: (k: string) => void;
}

// New fields spawn at a visible, staggered position (mm) instead of 0,0 so they
// appear on the canvas immediately and never pile up in the corner.
const defaultPos = (count: number) => ({ left: '30', top: `${30 + (count % 12) * 12}` });

const FieldToolbar = ({ data, setData, tab, setSelectedKey }: Props) => {
  const [fieldName, setFieldName] = useState('');
  const [groupName, setGroupName] = useState('');
  const [matrixName, setMatrixName] = useState('');
  const [radio, setRadio] = useState(false);

  const page = data[tab] || {};
  const count = Object.keys(page).length;
  const signatureExists = Object.keys(page).indexOf(SIGNATURE) > -1;

  const commit = (next: Record<string, any>, selectKey: string) => {
    setData(next);
    dispatch(setSaveIsDisabled(false));
    setSelectedKey(selectKey);
  };

  const addField = () => {
    if (!fieldName) return;
    const { left, top } = defaultPos(count);
    // The typed name is a LABEL, never the key — see builder/fieldKeys.ts.
    const key = newFieldKey(Object.keys(page));
    commit(
      {
        ...data,
        [tab]: {
          ...page,
          [key]: { label: fieldName, left, top, max: '', text: `${fieldName}-test`, isStatic: false },
        },
      },
      key,
    );
    setFieldName('');
  };

  const addSignature = () => {
    if (signatureExists) return;
    commit({ ...data, [tab]: { ...page, [SIGNATURE]: { left: '120', top: '260', text: '' } } }, SIGNATURE);
  };

  const addCheckboxGroup = () => {
    if (!groupName) return;
    const { left, top } = defaultPos(count);
    const key = newFieldKey(Object.keys(page));
    commit(
      {
        ...data,
        [tab]: {
          ...page,
          [key]: {
            label: groupName,
            inputType: radio ? 'radio' : 'checkbox',
            // Seed unticked. clearTemplateTexts blanks every option when the
            // template is attached to an inspection anyway, so seeding 'x' only
            // made the builder preview look pre-answered.
            items: [{ left, top, text: ' ', caption: 'LABEL', name: itemName(key, 0) }],
          },
        },
      },
      key,
    );
    setGroupName('');
    // Reset the toggle so the choice applies per-group and doesn't silently
    // carry over to the next group you add.
    setRadio(false);
  };

  const addMatrix = () => {
    if (!matrixName) return;
    const { left, top } = defaultPos(count);
    const key = newFieldKey(Object.keys(page));
    const gap = '10';
    const rows = '3';
    const columns = '4';
    commit(
      {
        ...data,
        [tab]: {
          ...page,
          [key]: {
            label: matrixName,
            matrix: createMatrixItems(gap, gap, top, left, rows, columns),
            matrixTop: top,
            matrixLeft: left,
            gapHorizontal: gap,
            gapVertical: gap,
            rows,
            columns,
          },
        },
      },
      key,
    );
    setMatrixName('');
  };

  return (
    <Wrapper>
      <Eyebrow>Add to page</Eyebrow>

      <Row>
        <Input
          placeholder="Field name"
          value={fieldName}
          onChange={e => setFieldName(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && addField()}
        />
        <AddBtn type="button" onClick={addField} disabled={!fieldName}>
          Add field
        </AddBtn>
      </Row>

      <GroupBox>
        <Row>
          <Input
            placeholder="Group name"
            value={groupName}
            onChange={e => setGroupName(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && addCheckboxGroup()}
          />
          <AddBtn type="button" onClick={addCheckboxGroup} disabled={!groupName}>
            Add group
          </AddBtn>
        </Row>
        <RadioRow>
          <input type="checkbox" checked={radio} onChange={e => setRadio(e.target.checked)} />
          <span>Make these radio buttons (single choice)</span>
        </RadioRow>
      </GroupBox>

      <Row>
        <Input
          placeholder="Table name"
          value={matrixName}
          onChange={e => setMatrixName(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && addMatrix()}
        />
        <AddBtn type="button" onClick={addMatrix} disabled={!matrixName}>
          Add table
        </AddBtn>
      </Row>

      <Divider />

      <SignatureBtn type="button" onClick={addSignature} disabled={signatureExists}>
        {signatureExists ? 'Signature added' : 'Add signature'}
      </SignatureBtn>
    </Wrapper>
  );
};

export default FieldToolbar;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 16px;
  border: 1px solid var(--border);
  border-radius: 10px;
  background: var(--neutral-50);
  box-sizing: border-box;
`;

const Eyebrow = styled.div`
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--neutral-500);
`;

const Row = styled.div`
  display: flex;
  gap: 8px;
`;

const GroupBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Input = styled.input`
  flex: 1;
  min-width: 0;
  height: 36px;
  padding: 0 10px;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--neutral-0);
  font-size: 0.8125rem;
  color: var(--mainText);
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: var(--primary-600);
    box-shadow: var(--shadow-focus);
  }
`;

const AddBtn = styled.button<{ $full?: boolean }>`
  flex: 0 0 auto;
  width: ${({ $full }) => ($full ? '100%' : 'auto')};
  height: 36px;
  padding: 0 14px;
  border: none;
  border-radius: 8px;
  background: var(--primary-600);
  color: var(--neutral-0);
  font-weight: 600;
  font-size: 0.8125rem;
  cursor: pointer;
  transition: background-color 0.15s ease;

  &:hover:not(:disabled) {
    background: var(--primary-700);
  }
  &:disabled {
    background: var(--neutral-300);
    cursor: not-allowed;
  }
`;

const RadioRow = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.8125rem;
  color: var(--neutral-700);
  margin-top: -2px;
  cursor: pointer;
`;

const Divider = styled.div`
  height: 1px;
  background: var(--border);
  margin: 2px 0;
`;

const SignatureBtn = styled.button`
  height: 36px;
  padding: 0 14px;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--neutral-0);
  color: var(--neutral-700);
  font-weight: 600;
  font-size: 0.8125rem;
  cursor: pointer;
  transition: background-color 0.15s ease, border-color 0.15s ease;

  &:hover:not(:disabled) {
    background: var(--primary-50);
    border-color: var(--primary-600);
    color: var(--primary-700);
  }
  &:disabled {
    color: var(--neutral-400);
    cursor: not-allowed;
  }
`;
